/**
 * Shadow DOM utilities for ABBA widget
 * Provides true CSS encapsulation while maintaining portal compatibility
 */

// Inline styles applied to the host element with `!important` so host-page
// CSS (including `* { ... !important }` resets and inherited body rules)
// cannot influence the values that cascade into the shadow tree.
const HOST_INLINE_STYLE = [
  'all: initial',
  'position: fixed',
  'bottom: 0',
  'right: 0',
  'width: 0',
  'height: 0',
  'z-index: 2147483647',
  'display: block',
  'box-sizing: border-box',
  'font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  'font-size: 14px',
  'font-weight: 400',
  'font-style: normal',
  'line-height: 1.5',
  'letter-spacing: normal',
  'text-align: left',
  'text-transform: none',
  'text-indent: 0',
  'color: #020817',
  '-webkit-font-smoothing: antialiased',
  '-moz-osx-font-smoothing: grayscale'
].map(decl => `${decl} !important`).join('; ')

const PORTAL_INLINE_STYLE = [
  'all: initial',
  'position: fixed',
  'inset: 0',
  'width: auto',
  'height: auto',
  'z-index: 2147483647',
  'display: block',
  'box-sizing: border-box',
  'pointer-events: none',
  'font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  'font-size: 14px',
  'font-weight: 400',
  'font-style: normal',
  'line-height: 1.5',
  'letter-spacing: normal',
  'text-align: left',
  'text-transform: none',
  'text-indent: 0',
  'color: #020817',
  '-webkit-font-smoothing: antialiased',
  '-moz-osx-font-smoothing: grayscale'
].map(decl => `${decl} !important`).join('; ')

export function createShadowContainer(hostElement: HTMLElement): ShadowRoot {
  // Lock down the host element so the host page's CSS cannot influence
  // properties that inherit through the shadow boundary.
  hostElement.setAttribute('style', HOST_INLINE_STYLE)

  const shadowRoot = hostElement.attachShadow({
    mode: 'open',
    delegatesFocus: true // Enables focus delegation for inputs
  })

  const mountPoint = document.createElement('div')
  mountPoint.id = 'shadow-mount'
  shadowRoot.appendChild(mountPoint)

  return shadowRoot
}

export function injectStyles(shadowRoot: ShadowRoot, cssContent: string): void {
  try {
    // Modern browsers: Constructable Stylesheets (better performance)
    const styleSheet = new CSSStyleSheet()
    styleSheet.replaceSync(cssContent)
    shadowRoot.adoptedStyleSheets = [styleSheet]
  } catch {
    // Fallback: <style> element for older browsers
    const styleElement = document.createElement('style')
    styleElement.textContent = cssContent
    shadowRoot.appendChild(styleElement)
  }
}

export function protectPortalContainer(container: HTMLElement): void {
  container.setAttribute('style', PORTAL_INLINE_STYLE)
}
